import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';
import { KeyIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const Profile = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');

  const { register: registerProfile, handleSubmit: handleProfileSubmit, formState: { errors: profileErrors, isSubmitting: isProfileSubmitting } } = useForm({
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
      'profile.phone': user?.profile?.phone || '',
      'profile.dateOfBirth': user?.profile?.dateOfBirth ? new Date(user.profile.dateOfBirth).toISOString().split('T')[0] : '',
      'profile.nid': user?.profile?.nid || '',
      'profile.bloodGroup': user?.profile?.bloodGroup || '',
      'profile.address': user?.profile?.address || '',
      'profile.designation': user?.profile?.designation || ''
    }
  });

  const { register: registerPassword, handleSubmit: handlePasswordSubmit, formState: { errors: passwordErrors, isSubmitting: isPasswordSubmitting }, reset: resetPassword } = useForm();

  const onProfileSubmit = async (data) => {
    const result = await updateProfile(data);
    if (result.success) {
      toast.success('Profile updated successfully');
    }
  };

  const onPasswordSubmit = async (data) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    const result = await changePassword(data.currentPassword, data.newPassword);
    if (result.success) {
      resetPassword();
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin': return 'role-admin';
      case 'director': return 'role-director';
      case 'accounts': return 'role-accounts';
      case 'employee': return 'role-employee';
      default: return 'role-employee';
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
        <p className="text-gray-600">Manage your account settings and preferences</p>
      </div>

      {/* Profile Header */}
      <div className="card p-6">
        <div className="flex items-center space-x-6">
          <div className="h-24 w-24 rounded-full bg-gray-200 flex items-center justify-center">
            <span className="text-2xl font-bold text-gray-600">
              {user?.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{user?.name}</h2>
            <p className="text-gray-600">{user?.email}</p>
            <div className="mt-2 flex items-center space-x-3">
              <span className={getRoleBadgeClass(user?.role)}>
                {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
              </span>
              <span className="text-sm text-gray-500">ID: {user?.employeeId}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'profile'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Profile Information
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'password'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Change Password
            </button>
            <button
              onClick={() => setActiveTab('financial')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'financial'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Financial Summary
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'profile' && (
            <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="label">Full Name</label>
                  <input
                    {...registerProfile('name', { required: 'Name is required' })}
                    type="text"
                    className="input-field"
                  />
                  {profileErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{profileErrors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="label">Email</label>
                  <input
                    {...registerProfile('email', { required: 'Email is required' })}
                    type="email"
                    className="input-field"
                    disabled
                  />
                </div>

                <div>
                  <label className="label">Phone</label>
                  <input
                    {...registerProfile('profile.phone')}
                    type="tel"
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="label">Date of Birth</label>
                  <input
                    {...registerProfile('profile.dateOfBirth')}
                    type="date"
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="label">NID</label>
                  <input
                    {...registerProfile('profile.nid')}
                    type="text"
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="label">Blood Group</label>
                  <select
                    {...registerProfile('profile.bloodGroup')}
                    className="input-field"
                  >
                    <option value="">Select Blood Group</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>

                <div>
                  <label className="label">Designation</label>
                  <input
                    {...registerProfile('profile.designation')}
                    type="text"
                    className="input-field"
                  />
                </div>
              </div>

              <div>
                <label className="label">Address</label>
                <textarea
                  {...registerProfile('profile.address')}
                  rows={3}
                  className="input-field"
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isProfileSubmitting}
                  className="btn-primary"
                >
                  {isProfileSubmitting ? 'Updating...' : 'Update Profile'}
                </button>
              </div>
            </form>
          )}

          {activeTab === 'password' && (
            <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-6 max-w-md">
              <div>
                <label className="label">Current Password</label>
                <input
                  {...registerPassword('currentPassword', { required: 'Current password is required' })}
                  type="password"
                  className="input-field"
                />
                {passwordErrors.currentPassword && (
                  <p className="mt-1 text-sm text-red-600">{passwordErrors.currentPassword.message}</p>
                )}
              </div>

              <div>
                <label className="label">New Password</label>
                <input
                  {...registerPassword('newPassword', {
                    required: 'New password is required',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' }
                  })}
                  type="password"
                  className="input-field"
                />
                {passwordErrors.newPassword && (
                  <p className="mt-1 text-sm text-red-600">{passwordErrors.newPassword.message}</p>
                )}
              </div>

              <div>
                <label className="label">Confirm New Password</label>
                <input
                  {...registerPassword('confirmPassword', { required: 'Please confirm your password' })}
                  type="password"
                  className="input-field"
                />
                {passwordErrors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{passwordErrors.confirmPassword.message}</p>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isPasswordSubmitting}
                  className="btn-primary flex items-center"
                >
                  <KeyIcon className="h-4 w-4 mr-2" />
                  {isPasswordSubmitting ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          )}

          {activeTab === 'financial' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6">
                <div className="card p-4">
                  <h3 className="text-sm font-medium text-gray-500">Salary</h3>
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatCurrency(user?.profile?.salary)}
                  </p>
                </div>
                <div className="card p-4">
                  <h3 className="text-sm font-medium text-gray-500">Outstanding Advance</h3>
                  <p className="text-2xl font-semibold text-orange-600">
                    {formatCurrency(user?.financials?.totalAdvance)}
                  </p>
                </div>
                <div className="card p-4">
                  <h3 className="text-sm font-medium text-gray-500">Outstanding Loan</h3>
                  <p className="text-2xl font-semibold text-red-600">
                    {formatCurrency(user?.financials?.totalLoan)}
                  </p>
                </div>
                <div className="card p-4">
                  <h3 className="text-sm font-medium text-gray-500">Submitted Expenses</h3>
                  <p className="text-2xl font-semibold text-blue-600">
                    {formatCurrency(user?.financials?.totalBillConveyance)}
                  </p>
                </div>
                <div className="card p-4">
                  <h3 className="text-sm font-medium text-gray-500">Paid Expenses</h3>
                  <p className="text-2xl font-semibold text-green-600">
                    {formatCurrency(user?.financials?.paidBillConveyance)}
                  </p>
                </div>
              </div>

              <div className="card p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Employment Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-500">Employee ID:</span>
                    <span className="ml-2 text-gray-900">{user?.employeeId}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Joining Date:</span>
                    <span className="ml-2 text-gray-900">
                      {user?.profile?.joiningDate 
                        ? new Date(user.profile.joiningDate).toLocaleDateString()
                        : 'N/A'
                      }
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Role:</span>
                    <span className={`ml-2 ${getRoleBadgeClass(user?.role)}`}>
                      {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Status:</span>
                    <span className="ml-2 text-green-600 font-medium">Active</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
